/* eslint-disable react/display-name */
import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import SectionComponent from '../components/Section/Section'
import { deleteSection, editSection, getSections, restaurantList } from '../apollo'
import {
  Box, Container, Typography, Paper, InputBase,
  IconButton, Button, Dialog, DialogTitle, DialogContent,
  CircularProgress, Chip, Switch, Menu, MenuItem, ListItemIcon
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import StorefrontIcon from '@mui/icons-material/Storefront'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import PeopleIcon from '@mui/icons-material/People'

const GET_SECTIONS = gql`${getSections}`
const EDIT_SECTION = gql`${editSection}`
const DELETE_SECTION = gql`${deleteSection}`
const GET_RESTAURANTS = gql`${restaurantList}`

const ActionMenu = ({ row, onEdit, onDelete }) => {
  const [anchor, setAnchor] = useState(null)
  return (
    <>
      <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget) }} sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#f1f5f9' } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9', minWidth: 140 } }}>
        <MenuItem onClick={() => { setAnchor(null); onEdit(row) }} sx={{ gap: 1.5, fontSize: '0.875rem' }}>
          <ListItemIcon sx={{ minWidth: 'auto' }}><EditIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon>
          <Typography fontSize="0.875rem" color="#3b82f6">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onDelete(row) }} sx={{ gap: 1.5, fontSize: '0.875rem' }}>
          <ListItemIcon sx={{ minWidth: 'auto' }}><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <Typography fontSize="0.875rem" color="#ef4444">Delete</Typography>
        </MenuItem>
      </Menu>
    </>
  )
}

const Sections = () => {
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [selectedSection, setSelectedSection] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const [mutateEdit] = useMutation(EDIT_SECTION)
  const [mutateDelete] = useMutation(DELETE_SECTION, { refetchQueries: [{ query: GET_SECTIONS }] })
  const { data, error, loading } = useQuery(GET_SECTIONS)
  const { data: dataRestaurants } = useQuery(GET_RESTAURANTS)

  const allSections = data?.sections ?? []
  const allRestaurants = dataRestaurants?.restaurantList ?? []
  const activeCount = allSections.filter(s => s.enabled).length
  const inactiveCount = allSections.length - activeCount

  const filtered = searchQuery.length < 2 ? allSections : allSections.filter(s =>
    (s.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  // restaurants in sections can be objects {_id, name} or plain IDs
  const getRestaurantId = r => (typeof r === 'string' ? r : r._id)
  const getRestaurantName = r => (typeof r === 'string' ? (allRestaurants.find(x => x._id === r)?.name ?? r) : r.name)

  return (
    <>
      {/* Hero banner */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #3b0764 60%, #581c87 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ViewModuleIcon sx={{ color: '#c4b5fd', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15 }}>Restaurant Sections</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Group restaurants into sections for your customer app</Typography>
            </Box>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={() => { setSelectedSection(null); setAddModal(true) }}
            sx={{ bgcolor: '#7c3aed', color: '#fff', fontWeight: 700, borderRadius: '12px', px: 2.5, py: 1.2, fontSize: '0.875rem', textTransform: 'none', '&:hover': { bgcolor: '#6d28d9' }, boxShadow: '0 4px 12px rgba(124,58,237,0.4)' }}>
            Add Section
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* Stat cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { label: 'Total Sections', value: allSections.length, icon: <ViewModuleIcon sx={{ color: '#7c3aed', fontSize: 26 }} />, bg: '#f5f3ff', border: '#ddd6fe' },
            { label: 'Active', value: activeCount, icon: <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 26 }} />, bg: '#f0fdf4', border: '#bbf7d0' },
            { label: 'Inactive', value: inactiveCount, icon: <PauseCircleIcon sx={{ color: '#d97706', fontSize: 26 }} />, bg: '#fffbeb', border: '#fde68a' },
            { label: 'Restaurants', value: allRestaurants.length, icon: <PeopleIcon sx={{ color: '#3b82f6', fontSize: 26 }} />, bg: '#eff6ff', border: '#bfdbfe' },
          ].map((card, i) => (
            <Paper key={i} elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${card.border}`, p: 2.5, display: 'flex', alignItems: 'center', gap: 2, background: card.bg, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                {card.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                  {loading ? <CircularProgress size={20} /> : card.value}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.3 }}>{card.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Table */}
        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
              All Sections{' '}
              <Typography component="span" sx={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}>({filtered.length})</Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', px: 1.5, py: 0.8, width: 260 }}>
              <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
              <InputBase placeholder="Search sections..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ fontSize: '0.875rem', flex: 1, color: '#475569' }} />
            </Box>
          </Box>

          {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              {/* Table header */}
              <Box sx={{ minWidth: 560, display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 60px', px: 3, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['Section', 'Status', 'Restaurants', ''].map((h, i) => (
                  <Typography key={i} sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</Typography>
                ))}
              </Box>

              {filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <ViewModuleIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ color: '#94a3b8' }}>No sections found</Typography>
                  <Typography sx={{ color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>Add a section to group your restaurants</Typography>
                </Box>
              ) : filtered.map((s, i) => (
                <Box key={s._id} sx={{ minWidth: 560, display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 60px', px: 3, py: 2, alignItems: 'center', borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none', '&:hover': { bgcolor: '#fafafa' } }}>
                  {/* Section name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <ViewModuleIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{s.name}</Typography>
                  </Box>

                  {/* Toggle */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                      size="small"
                      checked={!!s.enabled}
                      onChange={() => mutateEdit({ variables: { section: { _id: s._id, name: s.name, enabled: !s.enabled, restaurants: (s.restaurants ?? []).map(getRestaurantId) } } })}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#7c3aed' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#7c3aed' } }}
                    />
                    <Chip
                      label={s.enabled ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{ bgcolor: s.enabled ? '#f0fdf4' : '#f8fafc', color: s.enabled ? '#16a34a' : '#64748b', fontWeight: 600, fontSize: '0.72rem', height: 22 }}
                    />
                  </Box>

                  {/* Restaurants */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(s.restaurants ?? []).length === 0
                      ? <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>None assigned</Typography>
                      : (s.restaurants ?? []).slice(0, 3).map(r => (
                        <Chip key={getRestaurantId(r)} label={getRestaurantName(r)} size="small"
                          icon={<StorefrontIcon sx={{ fontSize: '12px !important', color: '#3b82f6 !important' }} />}
                          sx={{ bgcolor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: '0.7rem', height: 22, '& .MuiChip-icon': { ml: '5px' } }} />
                      ))
                    }
                    {(s.restaurants ?? []).length > 3 && (
                      <Chip label={`+${s.restaurants.length - 3} more`} size="small"
                        sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: '0.7rem', height: 22 }} />
                    )}
                  </Box>

                  {/* Actions */}
                  <ActionMenu
                    row={s}
                    onEdit={row => { setSelectedSection(row); setEditModal(true) }}
                    onDelete={row => mutateDelete({ variables: { id: row._id } })}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Add Modal */}
      <Dialog open={addModal} onClose={() => setAddModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Add New Section</Typography>
          <IconButton size="small" onClick={() => setAddModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <SectionComponent onClose={() => setAddModal(false)} />
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Edit Section</Typography>
          <IconButton size="small" onClick={() => setEditModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <SectionComponent section={selectedSection} onClose={() => setEditModal(false)} />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default withTranslation()(Sections)